import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete, Edit, Save, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { projectService, ProjectHistoricalUsageRow } from '../services/projectService';
import { useAuth } from '../contexts/AuthContext';

const currentYear = new Date().getFullYear();

const monthOptions = [
  { value: 1, label: 'Jan' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' }, { value: 4, label: 'Apr' },
  { value: 5, label: 'May' }, { value: 6, label: 'Jun' }, { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' },
  { value: 9, label: 'Sep' }, { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' },
];

const ProjectHistoricalUsagePage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { isSuperAdmin, hasPermission } = useAuth();
  const canAccess = isSuperAdmin || hasPermission('historical_usage');
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [usedMonth, setUsedMonth] = useState<number>(new Date().getMonth() + 1);
  const [usedYear, setUsedYear] = useState<number>(currentYear);
  const [note, setNote] = useState('');
  const [message, setMessage] = useState<string>('');
  const [editingId, setEditingId] = useState<string>('');
  const [editValue, setEditValue] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'MATCHED' | 'UNMATCHED'>('ALL');
  const [searchText, setSearchText] = useState('');
  const [monthFilter, setMonthFilter] = useState<number | 'ALL'>('ALL');
  const [yearFilter, setYearFilter] = useState<number | 'ALL'>('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data, isLoading, error } = useQuery({
    queryKey: ['project-historical-usage', projectId],
    queryFn: () => projectService.getHistoricalUsage(projectId!),
    enabled: !!projectId && canAccess,
  });

  const { data: projectDetails } = useQuery({
    queryKey: ['project-details', projectId],
    queryFn: () => projectService.getProject(projectId!),
    enabled: !!projectId && canAccess,
  });

  const uploadMutation = useMutation({
    mutationFn: () => projectService.uploadHistoricalUsage(projectId!, file!, usedMonth, usedYear, note),
    onSuccess: (result) => {
      setMessage(`Imported ${result.imported} rows. Matched ${result.matched}, unmatched ${result.unmatched}, skipped ${result.skippedEmpty}.`);
      setFile(null);
      queryClient.invalidateQueries({ queryKey: ['project-historical-usage', projectId] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { id: string; rawSiteInput: string }) =>
      projectService.updateHistoricalUsageRow(projectId!, payload.id, { rawSiteInput: payload.rawSiteInput }),
    onSuccess: () => {
      setEditingId('');
      setEditValue('');
      queryClient.invalidateQueries({ queryKey: ['project-historical-usage', projectId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => projectService.deleteHistoricalUsageRow(projectId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-historical-usage', projectId] });
    },
  });

  const years = useMemo(() => {
    const values: number[] = [];
    for (let y = currentYear; y >= currentYear - 10; y -= 1) values.push(y);
    return values;
  }, []);

  const filteredRows = useMemo(() => {
    const rows = data || [];
    const byStatus = rows.filter((row) => {
      if (statusFilter === 'MATCHED') return Boolean(row.matchedSiteId);
      if (statusFilter === 'UNMATCHED') return !row.matchedSiteId;
      return true;
    });
    const byMonthYear = byStatus.filter((row) => {
      if (monthFilter !== 'ALL' && row.usedMonth !== monthFilter) return false;
      if (yearFilter !== 'ALL' && row.usedYear !== yearFilter) return false;
      return true;
    });
    const q = searchText.trim().toLowerCase();
    if (!q) return byMonthYear;
    return byMonthYear.filter((row) =>
      row.rawSiteInput.toLowerCase().includes(q) ||
      row.normalizedDomain.toLowerCase().includes(q) ||
      (row.matchedSite?.site_url || '').toLowerCase().includes(q)
    );
  }, [data, statusFilter, monthFilter, yearFilter, searchText]);

  React.useEffect(() => {
    setPage(0);
  }, [statusFilter, monthFilter, yearFilter, searchText]);

  const totalCount = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  const pageButtons = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
    const result: (number | string)[] = [0];
    const start = Math.max(1, page - 1);
    const end = Math.min(totalPages - 2, page + 1);
    if (start > 1) result.push('...');
    for (let i = start; i <= end; i += 1) result.push(i);
    if (end < totalPages - 2) result.push('...');
    result.push(totalPages - 1);
    return result;
  }, [page, totalPages]);

  const handleDownloadDemoCsv = () => {
    const csv = [
      'site_url,note',
      'https://example.com,Used before tool onboarding',
      'blog.sampledomain.org,Legacy outreach placement',
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'project-historical-usage-demo.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!canAccess) {
    return <Alert severity="error">Only Admin and Super Admin can access this page.</Alert>;
  }

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/projects')}
        sx={{ alignSelf: 'flex-start', textTransform: 'none', color: '#64748b', mb: 0.5, '&:hover': { color: '#1e293b' } }}
      >
        Back to Projects
      </Button>
      <Typography sx={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-0.5px', mb: 0.5 }}>
        Project Historical Usage Upload
      </Typography>
      <Typography className="project-historical-project-name">
        Project: {projectDetails?.projectName || 'Loading...'}
      </Typography>
      <Typography sx={{ fontSize: '13px', color: 'var(--text-muted)' }}>
        Upload previous-month used guest blog sites. Matching is domain-only and counted in usage across all months.
      </Typography>

      <Paper
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          borderRadius: '12px',
          border: '1px solid #dbeafe',
          background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)',
          boxShadow: '0 1px 2px rgba(37,99,235,0.06)',
        }}
      >
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e40af' }}>
          Upload Historical Sheet
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Button variant="text" onClick={handleDownloadDemoCsv}>
            Download Demo CSV
          </Button>
          <Button variant="outlined" component="label">
            Select Sheet (CSV/XLSX)
            <input
              hidden
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>
          <TextField
            select
            label="Month"
            size="small"
            value={usedMonth}
            onChange={(e) => setUsedMonth(Number(e.target.value))}
            sx={{ minWidth: 120 }}
          >
            {monthOptions.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </TextField>
          <TextField
            select
            label="Year"
            size="small"
            value={usedYear}
            onChange={(e) => setUsedYear(Number(e.target.value))}
            sx={{ minWidth: 120 }}
          >
            {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
          <TextField
            label="Note (optional)"
            size="small"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ minWidth: 240 }}
          />
          <Button
            variant="contained"
            disabled={!file || uploadMutation.isPending || !projectId}
            onClick={() => uploadMutation.mutate()}
          >
            {uploadMutation.isPending ? 'Uploading...' : 'Upload & Match'}
          </Button>
          {file && <Typography variant="body2">{file.name}</Typography>}
        </Box>
      </Paper>

      <Paper sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search uploaded sites"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'ALL' | 'MATCHED' | 'UNMATCHED')}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="ALL">All</MenuItem>
          <MenuItem value="MATCHED">Matched only</MenuItem>
          <MenuItem value="UNMATCHED">Unmatched only</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Month"
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value === 'ALL' ? 'ALL' : Number(e.target.value))}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="ALL">All months</MenuItem>
          {monthOptions.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
        </TextField>
        <TextField
          select
          size="small"
          label="Year"
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value === 'ALL' ? 'ALL' : Number(e.target.value))}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="ALL">All years</MenuItem>
          {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        <Button
          variant="outlined"
          onClick={() => {
            setStatusFilter('ALL');
            setMonthFilter('ALL');
            setYearFilter('ALL');
            setSearchText('');
            setPage(0);
          }}
        >
          Clear Filters
        </Button>
      </Paper>

      {message && <Alert severity="success">{message}</Alert>}
      {error && <Alert severity="error">Failed to load historical usage rows.</Alert>}
      {uploadMutation.error && <Alert severity="error">Upload failed. Please check sheet format.</Alert>}

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: '12px',
          border: '1px solid var(--border-light)',
          boxShadow: 'var(--shadow)',
          overflow: 'hidden',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ background: '#f8fafc', fontWeight: 600 }}>Raw Site</TableCell>
              <TableCell sx={{ background: '#f8fafc', fontWeight: 600 }}>Domain</TableCell>
              <TableCell sx={{ background: '#f8fafc', fontWeight: 600 }}>Matched Site</TableCell>
              <TableCell sx={{ background: '#f8fafc', fontWeight: 600 }}>Month/Year</TableCell>
              <TableCell sx={{ background: '#f8fafc', fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ background: '#f8fafc', fontWeight: 600 }} align="center">Edit</TableCell>
              <TableCell sx={{ background: '#f8fafc', fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center"><CircularProgress size={22} /></TableCell>
              </TableRow>
            ) : paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">No historical usage rows yet.</TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row: ProjectHistoricalUsageRow) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{
                    '& td': { borderBottom: '1px solid var(--border-light)' },
                    '&:last-child td': { borderBottom: 'none' },
                  }}
                >
                  <TableCell sx={{ minWidth: 280 }}>
                    {editingId === row.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                      />
                    ) : row.rawSiteInput}
                  </TableCell>
                  <TableCell>{row.normalizedDomain}</TableCell>
                  <TableCell>{row.matchedSite?.site_url || '-'}</TableCell>
                  <TableCell>{row.usedMonth}/{row.usedYear}</TableCell>
                  <TableCell>{row.matchedSiteId ? 'Matched' : 'Unmatched'}</TableCell>
                  <TableCell align="center">
                    {editingId === row.id ? (
                      <Tooltip title="Save">
                        <IconButton
                          size="small"
                          onClick={() => updateMutation.mutate({ id: row.id, rawSiteInput: editValue })}
                          disabled={updateMutation.isPending}
                          sx={{
                            border: '1px solid var(--border)',
                            borderRadius: '8px',
                            width: 30,
                            height: 30,
                          }}
                        >
                          <Save fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Edit row">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingId(row.id);
                            setEditValue(row.rawSiteInput);
                          }}
                          sx={{
                            border: '1px solid var(--border)',
                            borderRadius: '8px',
                            width: 30,
                            height: 30,
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Delete row">
                      <IconButton
                        size="small"
                        onClick={() => deleteMutation.mutate(row.id)}
                        disabled={deleteMutation.isPending}
                        sx={{
                          border: '1px solid #fecaca',
                          color: '#dc2626',
                          borderRadius: '8px',
                          width: 30,
                          height: 30,
                          '&:hover': {
                            background: '#fef2f2',
                          },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </TableContainer>
    </Box>
  );
};

export default ProjectHistoricalUsagePage;
